import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { getFormattedPrice, useIntl } from '@finn/ui-utils';
import { useContext, useEffect } from 'react';

import {
  getForBusinessCookie,
  getMonthlyPaymentOfServiceFeeCookie,
} from '../../../helpers';
import { GenericVehicleDetails } from '../../core/types';
import { useConfigureStore } from '../../pdp-management';
import { VehicleContext } from '../../product-cards';
import { useCalculatePrice, usePreDiscount } from '../../product-pricing';
import { usePartnerDiscount } from './usePartnerDiscount';

type UseSubscriptionPrice = {
  trackPartnerDiscount?: boolean;
  closedUserGroupId?: string;
  vehicle?: GenericVehicleDetails;
};
export const useSubscriptionPrice = ({
  trackPartnerDiscount,
  closedUserGroupId,
  vehicle: overrideVehicle,
}: UseSubscriptionPrice = {}) => {
  const i18n = useIntl();
  const isForBusiness = getForBusinessCookie();
  const { vehicle } = useContext(VehicleContext);
  let {
    kilometerPackage,
    term,
    downPayment: isDownPayment,
    downPaymentAmount,
  } = useConfigureStore();
  const isMonthlyPaymentOfServiceFee = getMonthlyPaymentOfServiceFeeCookie();

  // Price for initial car in advanced grouping is fixed
  if (overrideVehicle) {
    term = overrideVehicle.default_downpayment_term;
    downPaymentAmount =
      overrideVehicle.downpayment_prices.downpayment_fixed_amount;
    kilometerPackage = overrideVehicle.downpayment_prices.kilometer_package;
    isDownPayment = true;
  }
  const partnerDiscount = usePartnerDiscount({
    closedUserGroupId,
  });
  const track = useTrackingStore((state) => state.track);

  useEffect(() => {
    if (partnerDiscount && trackPartnerDiscount) {
      track(TrackingEventName.PDP_PARTNER_DISCOUNT_SHOWED, {
        additionalProps: { partnerDiscount },
      });
    }
  }, [partnerDiscount, track]);

  const params = {
    term,
    vehicle: overrideVehicle ?? vehicle,
    isDownPayment,
    kilometerPackage,
    downPaymentAmount,
    isMonthlyPaymentOfServiceFee,
  };
  let totalPrice = useCalculatePrice(params);

  let originalPriceFormatted = usePreDiscount({
    vehicle: overrideVehicle ?? vehicle,
    price: totalPrice,
    term,
    km: kilometerPackage,
    isForBusiness,
    isMonthlyPaymentOfServiceFee,
    isDownPayment,
  });

  if (partnerDiscount > 0) {
    if (!originalPriceFormatted) {
      originalPriceFormatted = getFormattedPrice(i18n, totalPrice, 0);
    }
    totalPrice = totalPrice - totalPrice * partnerDiscount;
  }

  const finalPriceFormatted = getFormattedPrice(i18n, totalPrice, 0);

  return {
    partnerDiscount,
    finalPriceFormatted,
    originalPriceFormatted,
    isDownPayment,
    isForBusiness,
  };
};
